import { Caps } from '@/auth/roles';
import { asRecord } from '@/lib/json';

export const BOOL_CAPS = [Caps.CustomizePrompts, Caps.AuthorCustomAgents, Caps.AddTools, Caps.AutoApproveAllowed, Caps.DesignerAi] as const;

export const CAPABILITIES = ['runtime.ask', 'runtime.classify', 'runtime.extract', 'runtime.vision', 'designer.generate', 'designer.edit', 'designer.explain', 'designer.evals'];

export const PROVIDERS = ['anthropic', 'vertex-anthropic', 'vertex-gemini', 'openai-compatible'] as const;

/** Provider names configured as `Models:OpenAiCompatible:Endpoints:<name>` look like this (e.g. azure-openai-ch). */
const PROVIDER_NAME = /^[a-z0-9][a-z0-9.-]{1,63}$/;

/** Built-in providers plus any named endpoint already allowed for the tenant, in a stable order. */
export function providerOptions(selected: readonly string[]): string[] {
  const extra = selected.filter((p) => !(PROVIDERS as readonly string[]).includes(p)).sort();
  return [...PROVIDERS, ...extra];
}

/** Normalises a typed endpoint provider name; null when invalid. */
export function parseProviderName(input: string): string | null {
  const name = input.trim().toLowerCase();
  return PROVIDER_NAME.test(name) ? name : null;
}

export interface CapsState {
  flags: Record<string, boolean>;
  allowedProviders: string[];
  maxAgents: string;
  maxRunsPerDay: string;
}

export function capsFromJson(caps: unknown): CapsState {
  const c = asRecord(caps);
  const num = (v: unknown) => (typeof v === 'number' ? String(v) : '');
  return {
    flags: Object.fromEntries(BOOL_CAPS.map((k) => [k, c[k] === true])),
    allowedProviders: Array.isArray(c['allowedProviders']) ? (c['allowedProviders'] as unknown[]).filter((x): x is string => typeof x === 'string') : [],
    maxAgents: num(c['maxAgents']),
    maxRunsPerDay: num(c['maxRunsPerDay']),
  };
}

/** Builds the caps object (07 §1). Empty numeric limits are sent as null (= unlimited/platform default). */
export function capsToJson(s: CapsState): Record<string, unknown> {
  const toNum = (v: string) => (v.trim() === '' ? null : Number(v));
  return { ...s.flags, allowedProviders: s.allowedProviders, maxAgents: toNum(s.maxAgents), maxRunsPerDay: toNum(s.maxRunsPerDay) };
}

export function validateCaps(s: CapsState): string | null {
  for (const v of [s.maxAgents, s.maxRunsPerDay]) {
    if (v.trim() !== '' && !(Number.isInteger(Number(v)) && Number(v) >= 0)) return 'admin.validation.nonNegativeInt';
  }
  return null;
}

/** Per-capability model policy overrides (06 §3), platform.admin only. Edited as JSON with structural checks. */
export function validateModelPolicy(value: unknown): string | null {
  if (value === null) return null;
  if (typeof value !== 'object' || Array.isArray(value)) return 'admin.validation.jsonObject';
  for (const [cap, policy] of Object.entries(value as Record<string, unknown>)) {
    if (!CAPABILITIES.includes(cap)) return 'admin.modelPolicy.unknownCapability';
    const p = asRecord(policy);
    if (typeof p['provider'] !== 'string' || typeof p['model'] !== 'string') return 'admin.modelPolicy.providerModelRequired';
  }
  return null;
}
